import { constants } from "ethers";
import React, { FC, useEffect, useState } from 'react';
import { Typography, Box, Link, styled, useMediaQuery, Theme } from '@mui/material';
import useTitle from 'hooks/useTitle';
import PageContainer from 'pages/bridge/styled/PageContainer';
import Div from 'components/global/div';
import CMPImage from 'components/global/image';
import ReactCardFlip from "react-card-flip";
import { useContractRead } from "wagmi";
import MintBackground from '../../assets/images/mint-bg.jpg';
import GetLogger from "../../components/global/Logger";
import { ABIs, Addresses } from "../../constants/Addresses";
import Chains from "../../constants/wagmi/chains";
import { useWagmi } from "../../hooks/wagmi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectMintElvesPending } from '../../store/mint/hooks';
import { setMintElvesRefetch } from "../../store/mint/reducer";
import MintContainer from './_styles';
import ElvesCard from './components/ElvesCard';
import ElvesInfo from './components/ElvesInfo';
import ElvesLatestMint from './components/ElvesLatestMint';
import ElvesMint from './components/ElvesMint';
import ElvesMintPending from './components/ElvesMintPending';

const StyledInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  borderRadius: '16px',
  background: theme.palette.mintBg,
  padding: '16px 0',
  width: '150px',
  marginRight: 4,
  '&:last-child': {
    marginRight: 0,
  },
}));

const StyledCommunityLink = styled(Link)(({ theme }) => ({
  position: 'fixed',
  background: theme.palette.mintBg,
  border: `3px solid ${theme.palette.golden}`,
  zIndex: 999,
  bottom: 15,
  right: 15,
  padding: '8px 10px 10px 10px',
  borderRadius: 5,
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

const MintAlphaPage: FC = () => {
  const log = GetLogger('Elves Mint Page');
  const { address } = useWagmi();
  useTitle({ title: 'Mint' });
  const dispatch = useAppDispatch();
  const hiddenSm = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const [flipped, setFlipped] = useState(false);
  const [tokenIds, setTokenIds] = useState<number[]>([])
  const [ready, setReady] = useState<boolean>(false)
  const { refetch } = useContractRead({
    address: Addresses[Chains.avalanche.id].NPCs.ElvesMinter,
    abi: ABIs.NPCs.ElvesMinter,
    functionName: "pendingMintsOf",
    args: [address],
    enabled: address !== constants.AddressZero,
    watch: (tokenIds.length > 0 && !ready) ?? undefined,
    onSuccess: ([rawTokenIds, isReady]) => {
      const nTokenIds = rawTokenIds.map(t => t.toNumber())
      if ((nTokenIds.length > 0) !== flipped) {
        setFlipped(nTokenIds.length > 0)
      }
      if (ready !== isReady) {
        setReady(isReady);
      }

      if (!nTokenIds.every((t, i) => t === tokenIds[i])) {
        setTokenIds(nTokenIds)
      }
    },
    onError: (err) => {
      log.error(err)
    }
  })
  const handleConfirm = () => {
    refetch();
  };

  const handleRefetch = () => {
    refetch();
    dispatch(setMintElvesRefetch(true));
  }

  return (
    <PageContainer
      style={{
        backgroundImage: `url(${MintBackground})`,
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        margin: -16,
      }}
    >
      <MintContainer>
        <Div style={{ marginTop: 24 }}>
          <ElvesInfo />
        </Div>
        <Div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
          <StyledInfoBox>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              10,000
            </Typography>
            <Typography sx={{ fontSize: 18 }}>Elves</Typography>
          </StyledInfoBox>
          <StyledInfoBox>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              5
            </Typography>
            <Typography sx={{ fontSize: 18 }}>Unique Types</Typography>
          </StyledInfoBox>
          <StyledInfoBox>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              200+
            </Typography>
            <Typography sx={{ fontSize: 18 }}>Attributes</Typography>
          </StyledInfoBox>
        </Div>
        <Div>
          <ElvesCard />
        </Div>
        <Div style={{ margin: '40px 0' }}>
          <ElvesLatestMint />
        </Div>
        <ReactCardFlip isFlipped={flipped} flipDirection="horizontal">
          <ElvesMint onConfirm={handleConfirm} />
          <ElvesMintPending tokenIds={tokenIds} ready={ready} refetch={handleRefetch} />
        </ReactCardFlip>
      </MintContainer>
      {!hiddenSm && (
        <StyledCommunityLink href="https://discord.gg/cosmic-universe" target="_blank" rel="noreferrer">
          <Div>
            <Typography sx={{ fontSize: 14 }}>Need help?</Typography>
            <CMPImage
              asset={{
                src: 'data:image/png;base64,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',
                alt: 'join community',
              }}
              width={36}
              style={{ margin: 8 }}
            />
            <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>Join Discord</Typography>
          </Div>
        </StyledCommunityLink>
      )}
    </PageContainer>
  );
};

export default MintAlphaPage;
